import { useRef, useEffect, useState } from 'react';
import NavBar from '../components/layout/NavBar';
import Footer from '../components/layout/Footer';

const COOKING_POT_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/p4ryrjms_Cooking%20pot.png";
const WOK_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/lm6dnv3j_Cooking%20wok.png";
const COOKWARE_SET_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/2g4hkp6b_Cookware%20Set.png";

export const About = () => {
  const [heroProgress, setHeroProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const progress = Math.min(1, window.scrollY / window.innerHeight);
      setHeroProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const values = [
    {
      number: "01",
      title: "Precision",
      description: "Every curve, every angle, every millimeter is intentional. We engineer for performance, not appearance.",
    },
    {
      number: "02",
      title: "Integrity",
      description: "Premium materials throughout. No shortcuts, no compromise. What you see is what performs.",
    },
    {
      number: "03",
      title: "Longevity",
      description: "Built to outlast trends. Our 10-year warranty isn't marketing—it's a promise we stand behind.",
    },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-tex-bg">
      {/* Grain Overlay */}
      <div className="grain-overlay" />
      
      <NavBar />
      
      <main className="pt-20">
        {/* Hero Section - Pinned */}
        <section className="relative min-h-[200vh]">
          <div className="sticky top-0 h-screen overflow-hidden flex items-center justify-center">
            {/* Background */}
            <div 
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity: 0.15 + heroProgress * 0.1,
                transform: `scale(${1 + heroProgress * 0.1})`
              }}
            >
              <img 
                src={COOKWARE_SET_URL}
                alt="Tex-Style Company Cookware"
                className="w-full h-full object-contain object-center"
              />
            </div>
            
            <div 
              className="absolute inset-0"
              style={{
                background: `radial-gradient(ellipse at center, rgba(45, 26, 92, ${0.2 + heroProgress * 0.2}) 0%, transparent 60%)`
              }}
            />
            
            <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-left w-full">
              <p
                className="font-raleway text-[10px] font-semibold uppercase tracking-[0.5em] text-tex-violet-bright mb-6"
                style={{ opacity: 1 - heroProgress * 2 }}
              >
                About Tex-Style Company
              </p>
              
              <h1
                data-testid="about-heading"
                className="text-editorial-hero text-white"
              >
                We believe the kitchen
                <br />
                <span className="text-tex-violet-bright">deserves better.</span>
              </h1>
              
              <p
                className="font-raleway text-base md:text-lg font-light text-tex-muted max-w-2xl leading-relaxed mt-8"
                style={{ opacity: 1 - heroProgress * 2 }}
              >
                Tex-Style Company was born from a simple frustration: kitchenware that looks good 
                but doesn't perform, or performs well but looks industrial.
              </p>

              <div 
                className="flex items-center gap-4 mt-12"
                style={{ opacity: Math.max(0, 1 - heroProgress * 3) }}
              >
                <span className="font-raleway text-[10px] uppercase tracking-[0.3em] text-tex-muted/60">
                  Scroll
                </span>
                <div className="w-12 h-[1px] bg-gradient-to-r from-tex-violet to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-32 md:py-48">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-radial from-tex-violet/20 via-transparent to-transparent blur-3xl" />
                <img
                  src={COOKING_POT_URL}
                  alt="Tex-Style Company Cooking Pot"
                  className="relative z-10 w-full max-w-md mx-auto"
                />
              </div>
              
              <div className="space-y-8">
                <h2 className="text-editorial-section text-white text-left">
                  The art of cooking demands tools that <span className="text-tex-violet-bright">understand</span> it.
                </h2>
                <div className="space-y-6 font-raleway text-base font-light text-tex-muted leading-relaxed">
                  <p>
                    Our journey began in kitchens—professional and home—where we observed 
                    how cooks interact with their tools. The weight in their hands, the 
                    responsiveness of heat, the balance of a well-designed handle.
                  </p>
                  <p>
                    We partnered with metallurgists, chefs, and industrial designers to 
                    create kitchenware that performs at the highest level while maintaining 
                    a refined aesthetic that belongs in any kitchen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-32 md:py-48 relative">
          <div className="absolute inset-0 dot-matrix opacity-10" />
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
            <p className="font-raleway text-[10px] font-semibold uppercase tracking-[0.5em] text-tex-violet-bright mb-6">
              Our Principles
            </p>
            <h2 className="text-editorial-section text-white text-left mb-16">
              What we stand for
            </h2>

            <div className="space-y-12">
              {values.map((value, index) => (
                <div
                  key={value.title}
                  data-testid={`value-${index + 1}`}
                  className="flex gap-8 md:gap-12 items-start border-b border-white/5 pb-12"
                >
                  <span className="font-mono text-xs text-tex-violet-bright/70 mt-2">
                    {value.number}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-cormorant italic text-3xl md:text-4xl font-light text-white mb-4">
                      {value.title}
                    </h3>
                    <p className="font-raleway text-base font-light text-tex-muted leading-relaxed max-w-2xl">
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="py-32 md:py-48">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <p className="font-raleway text-[10px] font-semibold uppercase tracking-[0.5em] text-tex-violet-bright mb-6">
              The Collection
            </p>
            <h2 className="text-editorial-section text-white text-left mb-16">
              Crafted with intention
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="bg-tex-surface/20 border border-white/5 p-8 md:p-12 hover:border-tex-violet/30 transition-all duration-500 group">
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-gradient-radial from-tex-violet/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <img
                    src={COOKING_POT_URL}
                    alt="Tri-Ply Cooking Pot"
                    className="w-full max-w-xs mx-auto transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-cormorant italic text-2xl text-white mb-3">
                  The Everyday Essentials
                </h3>
                <p className="font-raleway text-sm font-light text-tex-muted leading-relaxed">
                  Tri-ply construction. Glass lids for visibility. Ergonomic handles for comfort.
                </p>
              </div>

              <div className="bg-tex-surface/20 border border-white/5 p-8 md:p-12 hover:border-tex-violet/30 transition-all duration-500 group">
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-gradient-radial from-tex-violet/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <img
                    src={WOK_URL}
                    alt="Stainless Steel Wok"
                    className="w-full max-w-xs mx-auto transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-cormorant italic text-2xl text-white mb-3">
                  Professional Performance
                </h3>
                <p className="font-raleway text-sm font-light text-tex-muted leading-relaxed">
                  High-heat resistant. Honeycomb non-stick interior. Balanced for precise control.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-32 md:py-48 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial from-tex-violet-deep/40 via-transparent to-transparent" />
          <div className="absolute inset-0 dot-matrix opacity-10" />
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
            <h2 className="text-editorial-section text-white text-left mb-8">
              Ready to elevate
              <br />
              your kitchen?
            </h2>
            <a
              href="https://shop.texstyleco.com"
              className="btn-primary inline-block"
            >
              Explore the Collection
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
